import React, { useState, useEffect, useCallback, useRef, memo } from "react";
import PropTypes from "prop-types";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  DEFAULT_CROP,
  HEADER_HEIGHT,
  TOOLBAR_HEIGHT,
  CROP_IMAGE_STYLE,
  PDF_VIEWER_STYLE,
  ASPECT_OPTIONS,
  JPEG_QUALITY,
  ALLOW_IMAGE_EDITOR_ROTATE,
} from "./constants";
import { getCroppedImageFromOriginal } from "./utils";
import { useImageType, useImageEditorRotation } from "./hooks/useImageEditor";

// -----------------------------------------------------------------------------
// Subcomponents (single responsibility, memoized to reduce re-renders)
// -----------------------------------------------------------------------------

const CONTAINER_STYLE = {
  width: "100dvw",
  height: "100dvh",
  backgroundColor: "#0a0a0a",
  top: 0,
  left: 0,
  zIndex: 1100,
  position: "fixed",
  display: "flex",
  flexDirection: "column",
};

const HEADER_STYLE = {
  flexShrink: 0,
  height: HEADER_HEIGHT,
  minHeight: HEADER_HEIGHT,
  paddingLeft: "max(12px, env(safe-area-inset-left))",
  paddingRight: "max(12px, env(safe-area-inset-right))",
  paddingTop: "max(8px, env(safe-area-inset-top))",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, transparent 100%)",
};

const ImageEditorHeader = memo(function ImageEditorHeader({ onCancel, onDone, isDoneDisabled }) {
  return (
    <header style={HEADER_STYLE} className="flex-nowrap">
      <button
        type="button"
        onClick={onCancel}
        className="flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-white hover:bg-white/10 active:bg-white/15 rounded-full transition-colors text-base font-medium"
        aria-label="Cancel"
      >
        Cancel
      </button>
      <h1 className="flex-1 min-w-0 text-center text-white font-semibold text-lg truncate px-2">
        Edit
      </h1>
      <button
        type="button"
        onClick={onDone}
        disabled={isDoneDisabled}
        className="flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center text-emerald-400 hover:bg-white/10 active:bg-white/15 rounded-full transition-colors text-base font-semibold disabled:opacity-50 disabled:pointer-events-none"
        aria-label="Done"
      >
        Done
      </button>
    </header>
  );
});

ImageEditorHeader.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
  isPdf: PropTypes.bool,
  isDoneDisabled: PropTypes.bool,
};

const CropView = memo(function CropView({
  displayImage,
  crop,
  onCropChange,
  onCropComplete,
  onImageLoaded,
  aspectChoice,
  disabled,
}) {
  return (
    <ReactCrop
      src={displayImage}
      crop={crop}
      onChange={onCropChange}
      onComplete={onCropComplete}
      onImageLoaded={onImageLoaded}
      keepSelection
      aspect={aspectChoice}
      imageStyle={CROP_IMAGE_STYLE}
      className="max-w-full"
      disabled={disabled}
    />
  );
});

CropView.propTypes = {
  displayImage: PropTypes.string,
  crop: PropTypes.object.isRequired,
  onCropChange: PropTypes.func.isRequired,
  onCropComplete: PropTypes.func.isRequired,
  onImageLoaded: PropTypes.func.isRequired,
  aspectChoice: PropTypes.number,
  disabled: PropTypes.bool,
};

const IFRAME_STYLE = {
  width: "100%",
  height: "100%",
  minHeight: "70vh",
  border: "none",
  backgroundColor: "#fff",
};

/**
 * Chrome blocks data: URLs in iframes for PDFs ("This page has been blocked").
 * Use a blob URL instead so the native PDF viewer loads.
 */
function dataUrlToBlobUrl(dataUrl) {
  return fetch(dataUrl)
    .then((r) => r.blob())
    .then((blob) => URL.createObjectURL(blob));
}

const PdfView = memo(function PdfView({ url }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const blobUrlRef = useRef(null);

  useEffect(() => {
    if (!url) {
      setBlobUrl(null);
      return;
    }
    if (url.startsWith("data:")) {
      let cancelled = false;
      dataUrlToBlobUrl(url)
        .then((created) => {
          if (!cancelled) {
            blobUrlRef.current = created;
            setBlobUrl(created);
          } else {
            URL.revokeObjectURL(created);
          }
        })
        .catch(() => setBlobUrl(url));
      return () => {
        cancelled = true;
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }
      };
    }
    blobUrlRef.current = null;
    setBlobUrl(url);
    return () => {};
  }, [url]);

  const src = blobUrl || (url && !url.startsWith("data:") ? url : null);
  if (!src) return null;

  return (
    <div className="w-full flex justify-center flex-1 min-h-0" style={PDF_VIEWER_STYLE}>
      <iframe title="PDF preview" src={src} style={IFRAME_STYLE} />
    </div>
  );
});

PdfView.propTypes = {
  url: PropTypes.string,
};

const TOOLBAR_STYLE = {
  minHeight: TOOLBAR_HEIGHT,
  paddingLeft: "max(16px, env(safe-area-inset-left))",
  paddingRight: "max(16px, env(safe-area-inset-right))",
  paddingTop: 12,
  paddingBottom: "max(12px, env(safe-area-inset-bottom))",
};

const ImageEditorToolbar = memo(function ImageEditorToolbar({
  onReupload,
  onRecapture,
  onRotate,
  isRotating,
  aspectChoice,
  onAspectChange,
}) {
  return (
    <div
      className="flex flex-col gap-3 flex-shrink-0 border-t border-white/10 bg-black/50"
      style={TOOLBAR_STYLE}
    >
      <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-between">
        <div className="flex flex-wrap items-center justify-center gap-2 min-w-0 flex-1 sm:flex-initial">
          {onReupload && (
            <button
              type="button"
              onClick={onReupload}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-full bg-white/15 text-white hover:bg-white/25 text-xs sm:text-sm font-medium transition-colors flex-shrink-0 min-h-[44px]"
              aria-label="Re-upload image"
            >
              <UploadIcon />
              <span>Re-upload</span>
            </button>
          )}
          {onRecapture && (
            <button
              type="button"
              onClick={onRecapture}
              className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-full bg-white/15 text-white hover:bg-white/25 text-xs sm:text-sm font-medium transition-colors flex-shrink-0 min-h-[44px]"
              aria-label="Re-capture with camera"
            >
              <CameraIcon />
              <span>Re-capture</span>
            </button>
          )}
        </div>
        {onRotate != null && (
          <button
            type="button"
            onClick={onRotate}
            disabled={isRotating}
            className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-full bg-white/15 text-white hover:bg-white/25 disabled:opacity-50 text-xs sm:text-sm font-medium transition-colors flex-shrink-0 min-h-[44px]"
            aria-label="Rotate 90°"
            aria-busy={isRotating}
          >
            {isRotating ? (
              <span
                className="w-5 h-5 flex-shrink-0 border-2 border-white/40 border-t-white rounded-full animate-spin"
                aria-hidden
              />
            ) : (
              <RotateIcon />
            )}
            <span>Rotate</span>
          </button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {ASPECT_OPTIONS.map((opt) => (
          <button
            key={opt.label}
            type="button"
            onClick={() => onAspectChange(opt.value)}
            className={`px-2.5 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-colors min-h-[36px] flex items-center justify-center ${
              aspectChoice === opt.value
                ? "bg-amber-500 text-black"
                : "bg-white/15 text-white hover:bg-white/25"
            }`}
            aria-pressed={aspectChoice === opt.value}
            aria-label={`Aspect ratio ${opt.label}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
});

ImageEditorToolbar.propTypes = {
  onReupload: PropTypes.func,
  onRecapture: PropTypes.func,
  onRotate: PropTypes.func,
  isRotating: PropTypes.bool,
  aspectChoice: PropTypes.number,
  onAspectChange: PropTypes.func.isRequired,
};

function UploadIcon() {
  return (
    <svg
      className="w-5 h-5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
      />
    </svg>
  );
}

function CameraIcon() {
  return (
    <svg
      className="w-5 h-5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 13v4a2 2 0 01-2 2H7a2 2 0 01-2-2v-4M14 9v4"
      />
    </svg>
  );
}

function RotateIcon() {
  return (
    <svg
      className="w-5 h-5 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
      />
    </svg>
  );
}

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const ImageEditor = ({ image, onCancel, onEdit, onReupload, onRecapture }) => {
  const [originalImage, setOriginalImage] = useState(null);
  const [crop, setCrop] = useState(DEFAULT_CROP);
  const [aspectChoice, setAspectChoice] = useState(undefined);
  const imageRef = useRef(null);
  const completedCropRef = useRef(null);

  const { isPdf, isCheckingType } = useImageType(image);
  const { displayImage, rotationDegrees, isRotating, handleRotate } = useImageEditorRotation(
    originalImage,
    isPdf
  );

  useEffect(() => {
    if (!image) {
      setOriginalImage(null);
      setCrop(DEFAULT_CROP);
      setAspectChoice(undefined);
      completedCropRef.current = null;
      return;
    }
    setOriginalImage(image);
    setCrop(DEFAULT_CROP);
    setAspectChoice(undefined);
    completedCropRef.current = null;
  }, [image]);

  useEffect(() => {
    completedCropRef.current = null;
    setCrop(DEFAULT_CROP);
  }, [rotationDegrees]);

  const handleImageLoaded = useCallback((imgNode) => {
    imageRef.current = imgNode;
  }, []);

  const handleCropComplete = useCallback((c) => {
    completedCropRef.current = c;
  }, []);

  // Uses imageRef.current and completedCropRef.current intentionally (refs are stable).
  // If you add any state reads inside this callback, add them to the dependency array.
  const getCroppedImage = useCallback(() => {
    const node = imageRef.current;
    const completed = completedCropRef.current;
    if (!node || !completed?.width || !completed?.height) return;

    if (rotationDegrees !== 0) {
      getCroppedImageFromOriginal(
        originalImage,
        rotationDegrees,
        completed,
        node.naturalWidth,
        node.naturalHeight
      )
        .then(onEdit)
        .catch((err) => {
          console.error("Crop failed:", err);
        });
      return;
    }

    const canvas = document.createElement("canvas");
    const scaleX = node.naturalWidth / node.width;
    const scaleY = node.naturalHeight / node.height;
    canvas.width = Math.round(completed.width);
    canvas.height = Math.round(completed.height);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(
      node,
      completed.x * scaleX,
      completed.y * scaleY,
      completed.width * scaleX,
      completed.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );
    onEdit(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
  }, [onEdit, originalImage, rotationDegrees]);

  const handleCancel = useCallback(() => {
    setOriginalImage(null);
    onCancel();
  }, [onCancel]);

  const handleDone = useCallback(() => {
    if (isPdf) {
      onEdit(displayImage ?? originalImage);
    } else {
      getCroppedImage();
    }
  }, [isPdf, displayImage, originalImage, onEdit, getCroppedImage]);

  const handleAspectChange = useCallback((value) => {
    setAspectChoice(value);
    const img = imageRef.current;
    if (value == null || value === undefined) {
      setCrop({ ...DEFAULT_CROP, aspect: undefined });
      completedCropRef.current = null;
      return;
    }
    if (!img?.naturalWidth || !img?.naturalHeight) {
      setCrop({ ...DEFAULT_CROP, aspect: value });
      completedCropRef.current = null;
      return;
    }
    const W = img.naturalWidth;
    const H = img.naturalHeight;
    const A = Number(value);
    let cropW, cropH;
    if (W >= A * H) {
      cropH = 100;
      cropW = (100 * A * H) / W;
    } else {
      cropW = 100;
      cropH = (100 * W) / (A * H);
    }
    const x = (100 - cropW) / 2;
    const y = (100 - cropH) / 2;
    setCrop({ unit: "%", width: cropW, height: cropH, x, y, aspect: value });
    completedCropRef.current = null;
  }, []);

  if (!image) return null;

  return (
    <div style={CONTAINER_STYLE}>
      <ImageEditorHeader
        onCancel={handleCancel}
        onDone={handleDone}
        isDoneDisabled={!isPdf && isRotating}
      />

      <div className="flex-1 min-h-0 flex items-center justify-center overflow-auto py-4">
        {isCheckingType && <div className="text-white/80 text-sm">Loading…</div>}
        {!isCheckingType && !isPdf && displayImage && (
          <CropView
            displayImage={displayImage}
            crop={crop}
            onCropChange={setCrop}
            onCropComplete={handleCropComplete}
            onImageLoaded={handleImageLoaded}
            aspectChoice={aspectChoice}
            disabled={isRotating}
          />
        )}
        {!isCheckingType && isPdf && <PdfView url={displayImage ?? originalImage} />}
      </div>

      {!isCheckingType && !isPdf && (
        <ImageEditorToolbar
          onReupload={onReupload}
          onRecapture={onRecapture}
          onRotate={ALLOW_IMAGE_EDITOR_ROTATE ? handleRotate : undefined}
          isRotating={isRotating}
          aspectChoice={aspectChoice}
          onAspectChange={handleAspectChange}
        />
      )}
    </div>
  );
};

ImageEditor.propTypes = {
  image: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onReupload: PropTypes.func,
  onRecapture: PropTypes.func,
};

ImageEditor.defaultProps = {
  image: null,
  onReupload: undefined,
  onRecapture: undefined,
};

export default memo(ImageEditor);
